import type { GatewayEntry } from '../managers/gateway-entry';

/*
 * WHICH GATEWAY A CALL IS ABOUT — resolved from the ORG'S FULL ROSTER, never from what happens to
 * be on screen.
 *
 * Every gateway-scoped tool used to read `entry()` — the ONE gateway currently selected in this
 * browser's viewport — and refuse "no gateway is open" for any other. That coupled the assistant's
 * reach to a human's navigation state, which is backwards: the org's other gateways are already
 * loaded (`entries()` is the shell's full reconciled fleet, every claimed box, connected whether or
 * not it is on screen — see app-shell's `resolveFleet`), and the worker's own RPCs already take an
 * explicit gateway id per call rather than assuming a "current" one. The viewport restriction was a
 * wiring choice in these tools, not a limit anywhere below them.
 *
 * The fix is not "let the assistant drive navigation to unblock itself" — that fixes the symptom by
 * hijacking the one thing a person is looking at, is undone the moment they navigate away, and reads
 * exactly as strange as it is: an AI should not need to move a human's screen to go read something.
 * The fix is to give every tool the same `gateway` parameter a search result already names, resolved
 * against the roster directly. `selected()` remains ONLY as the fallback for when the caller does
 * not name one — the ordinary case of "the thing on screen", not a requirement.
 */

export interface GatewayScope {
  /** The org's full roster — every gateway it owns, each already connected. */
  entries(): readonly GatewayEntry[];
  /** What's open in the browser right now, if anything — used only when `gateway` is omitted. */
  selected(): GatewayEntry | undefined;
}

export interface ResolvedGateway {
  entry: GatewayEntry;
  id: string;
  name: string;
}

export type GatewayResolution = { ok: true; value: ResolvedGateway } | { ok: false; message: string };

function nameOf(entry: GatewayEntry): string {
  const g = entry.gateway;
  return g.displayName || g.hostname || g.deviceFingerprint;
}

/** The `gateway` parameter every gateway-scoped tool takes — id or name, case-insensitive,
 *  matched against the org's whole roster. Shared so the wording is identical everywhere. */
export const GATEWAY_PARAM = {
  type: 'string' as const,
  description:
    'Which relay, by id or name — from gateways.list/gateways.search. Case-insensitive; ' +
    'partial names are not matched here, use gateways.search for that. Omit to use whichever relay ' +
    'is currently open in the browser.',
};

export function resolveGateway(scope: GatewayScope, wanted: unknown): GatewayResolution {
  const query = typeof wanted === 'string' ? wanted.trim() : '';
  if (query) {
    const q = query.toLowerCase();
    const hit = scope.entries().find((e) => e.gateway.deviceFingerprint.toLowerCase() === q || nameOf(e).toLowerCase() === q);
    if (hit) return { ok: true, value: { entry: hit, id: hit.gateway.deviceFingerprint, name: nameOf(hit) } };
    const known = scope.entries().map(nameOf);
    return {
      ok: false,
      message: known.length
        ? `No relay matches "${query}". This organization has: ${known.join(', ')}. Use gateways.search for a looser match.`
        : 'This organization has no relays yet.',
    };
  }
  const sel = scope.selected();
  if (sel) return { ok: true, value: { entry: sel, id: sel.gateway.deviceFingerprint, name: nameOf(sel) } };
  return {
    ok: false,
    message: 'No relay is open in the browser and none was named. Pass `gateway` — an id or name from gateways.list.',
  };
}

/**
 * WHY A PATH THE ROUTER ACCEPTS STILL NAMES NOTHING — or '' when it names a real place.
 *
 * The router checks a path's SHAPE: `/gateways/x/controllers/y` is a page whatever x and y are. So a
 * path written with a relay's NAME where its id belongs (the gateway parameter takes either, which
 * is how the model learned to write "Miyu's MacBook"), or with a controller that is not there, opened
 * an empty page and the tool said it had taken the person to it. The ids are checked here against the
 * roster the other tools resolve through, and a name is answered with the id it should have been.
 *
 * Only what is known is held against a path: while the roster, or a relay's controller list, is still
 * empty there is nothing to check against, and refusing would turn a slow load into a wrong answer.
 */
export function unknownPlace(scope: GatewayScope, place: { gatewayId?: string; controllerId?: string }): string {
  const entries = scope.entries();
  if (!place.gatewayId || !entries.length) return '';
  const wanted = place.gatewayId.toLowerCase();
  const entry = entries.find((e) => e.gateway.deviceFingerprint.toLowerCase() === wanted);
  if (!entry) {
    const named = entries.find((e) => nameOf(e).toLowerCase() === wanted);
    if (named) return `"${place.gatewayId}" is a relay's name; the path takes its id: /gateways/${named.gateway.deviceFingerprint}.`;
    return `No relay has the id "${place.gatewayId}". This organization has: ${entries.map((e) => `${nameOf(e)} (${e.gateway.deviceFingerprint})`).join(', ')}.`;
  }
  const controllers = entry.controllers.all;
  if (!place.controllerId || !controllers.length || controllers.some((c) => c.id === place.controllerId)) return '';
  const named = controllers.find((c) => c.name.toLowerCase() === place.controllerId!.toLowerCase());
  if (named) return `"${place.controllerId}" is a controller's name; the path takes its id: ${named.id}.`;
  return `${nameOf(entry)} has no controller "${place.controllerId}". Its controllers are: ${controllers.map((c) => `${c.name} (${c.id})`).join(', ')}.`;
}
